
import {
  defineComponent,
  onMounted,
  ref,
  Ref,
  computed,
  PropType,
} from 'vue';

import Breadcrumb from 'primevue/breadcrumb';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import SelectButton from 'primevue/selectbutton';
import FileUpload, { FileUploadUploaderEvent } from 'primevue/fileupload';
import Toolbar from 'primevue/toolbar';

import FileItem from '@/components/file-manager/FileItem.vue';
import FolderItem from '@/components/file-manager/FolderItem.vue';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import InputTextModal from '@/components/modals/InputTextModal.vue';
import ImageCompressionModal from '@/components/modals/ImageCompressionModal.vue';

import { useToast } from 'primevue/usetoast';
import ProgressSpinner from 'primevue/progressspinner';
import { MenuItem } from 'primevue/menuitem';

import { UserState } from '@/composables/User';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { getTemplateParentTypeByType } from '@/components/template-builder/utils/helpers';
import {
  getFilesFromPath,
  createFolderInMedia,
  createNewFileInMedia,
} from '@/composables/configs/configuration';

import { extensions as extensionsList } from '@/configs/file-manager';

import { File as MediaFile, Folder } from '@/types';
import { TemplateParentTypeEnum } from '@/types/enums';

import { showToastError } from '@/helpers';

import { useI18n } from 'vue-i18n';
import { folderNameValidator } from '@/helpers/CustomValidator';

export default defineComponent({
  name: 'FilesList',

  components: {
    Breadcrumb,
    Button,
    Divider,
    FileUpload,
    ProgressSpinner,
    FileItem,
    FolderItem,
    Toolbar,
    SpmOverlayPanel,
    SpmPanelMenu,
    InputTextModal,
    ImageCompressionModal,
    SelectButton,
  },

  props: {
    extensions: {
      type: String,
      required: false,
      default: '',
    },
    uploadContext: {
      type: String as PropType<'email' | 'display' | 'file-manager'>,
      required: false,
      default: 'file-manager',
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-file': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const files: Ref<MediaFile[]> = ref([]);
    const folders: Ref<Folder[]> = ref([]);
    const idShop: number = UserState.activeShop ? UserState.activeShop.id : 0;
    const data = ref('');
    const toast = useToast();

    const loading = ref(false);
    const loadingNextPage = ref(false);

    const displaySpinner = ref(false);

    const chooseFileBtnRef = ref();

    const newFolderName: Ref<string> = ref('');
    const displayNewFolderModal = ref(false);
    const currentPath = ref('');

    const displayCompressionModal = ref(false);
    const currentImageFile: Ref<globalThis.File | null> = ref(null);
    const pendingFiles: Ref<globalThis.File[]> = ref([]);

    // Détecter automatiquement le contexte depuis le template
    const detectedContext = computed(() => {
      const templateType = getTemplateParentTypeByType(TemplateEditorState.template?.type);

      if (templateType === TemplateParentTypeEnum.EMAIL) {
        return 'email';
      } else if (templateType === TemplateParentTypeEnum.DISPLAY) {
        return 'display';
      }
      return props.uploadContext || 'file-manager';
    });

    const layout = ref('grid');
    const layoutOptions = ref(['list', 'grid']);

    const handleChooseFile = (params: any) => {
      context.emit('on-choose-file', params);
      if (params.action === 'download') {
        context.emit('on-close-dialog');
      }
    };

    const handleDeleteItem = async (fileId: string) => {
      files.value = files.value.filter((file: MediaFile) => file.id !== fileId);
    };

    const handleMoveItem = async (sameFile: boolean, fileId: string) => {
      if (!sameFile) {
        files.value = files.value.filter((file: MediaFile) => file.id !== fileId);
      }
    };

    const convertImageToBase64 = async (file: any) => new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const base64Data = e.target.result;
        data.value = base64Data;
        resolve(base64Data);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      // Read the image file as a data URL
      reader.readAsDataURL(file);
    });

    const isImageFile = (file: globalThis.File): boolean => {
      return file?.type?.startsWith('image/') || false;
    };

    const compressImageQuick = async (file: globalThis.File): Promise<{ compressedFile: globalThis.File; worthIt: boolean }> => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const img = new Image();
          img.onload = () => {
            let { width, height } = img;
            let quality = 0.85;
            const context = detectedContext.value;

            if (context === 'email') {
              // Email : max 800px de large (standard email)
              const maxWidth = 900;

              // Ne réduire que si l'image est plus large que 800px
              if (width > maxWidth) {
                const ratio = maxWidth / width;
                width = maxWidth;
                height = Math.round(height * ratio);
              }

              // Qualité selon taille du fichier
              if (file.size > 2 * 1024 * 1024) { // > 2MB
                quality = 0.7;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                quality = 0.75;
              } else if (file.size > 500 * 1024) { // > 500KB
                quality = 0.8;
              } else {
                quality = 0.85;
              }
            } else if (context === 'display') {
              // Display : privilégier la qualité
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.6;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            } else {
              // File Manager : modéré (défaut)
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.5;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.6;
                quality = 0.8;
              } else if (file.size > 500 * 1024) { // > 500KB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve({ compressedFile: file, worthIt: false });
              return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve({ compressedFile: file, worthIt: false });
                  return;
                }
                const compressedFile = new globalThis.File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                // Compression bénéfique si réduction d'au moins 5% OU 50 KB
                const reductionPercent = ((file.size - compressedFile.size) / file.size) * 100;
                const reductionBytes = file.size - compressedFile.size;
                const worthIt = reductionPercent >= 5 || reductionBytes >= 51200; // 5% ou 50KB
                resolve({ compressedFile, worthIt });
              },
              file.type,
              quality
            );
          };
          img.onerror = () => resolve({ compressedFile: file, worthIt: false });
          img.src = e.target.result;
        };
        reader.onerror = () => resolve({ compressedFile: file, worthIt: false });
        reader.readAsDataURL(file);
      });
    };

    const uploadFilesToMedia = async (filesToUpload: globalThis.File[]) => {
      try {
        const dataFiles: string[] = [];
        displaySpinner.value = true;

        for (const file of filesToUpload) {
          // eslint-disable-next-line no-await-in-loop
          await convertImageToBase64(file);
          // eslint-disable-next-line no-await-in-loop
          dataFiles.push(data.value);
        }
        const uploadResult = await createNewFileInMedia(dataFiles, idShop, currentPath.value);

        uploadResult.data.forEach((element: any) => {
          const { id, path, type } = element;

          const tempFile: MediaFile = {
            id,
            url: `https://media.shopimind.io/clients/${path}`,
            base_url: path,
            type,
          };
          files.value.unshift(tempFile);
        });
      } catch (error) {
        toast.add({
          severity: 'error', summary: t('error'), detail: t('templateBuilder.panels.fileManager.updateError'), life: 3000,
        });
      } finally {
        displaySpinner.value = false;
      }
    };

    const handleUpload = async (event: FileUploadUploaderEvent) => {
      if (event.files && Array.isArray(event.files)) {
        // Séparer les images des autres fichiers
        const imageFiles = event.files.filter(isImageFile);
        const nonImageFiles = event.files.filter(file => !isImageFile(file));

        // Upload direct des fichiers non-image
        if (nonImageFiles.length > 0) {
          await uploadFilesToMedia(nonImageFiles);
        }

        // Traiter les images
        if (imageFiles.length > 0) {
          const filesToShowModal: globalThis.File[] = [];
          const filesToUploadDirectly: globalThis.File[] = [];

          // Pré-compression rapide pour déterminer quelles images montrer dans la modale
          for (const file of imageFiles) {
            // eslint-disable-next-line no-await-in-loop
            const { worthIt } = await compressImageQuick(file);
            if (worthIt) {
              filesToShowModal.push(file);
            } else {
              filesToUploadDirectly.push(file);
            }
          }

          // Upload direct des images déjà optimisées
          if (filesToUploadDirectly.length > 0) {
            await uploadFilesToMedia(filesToUploadDirectly);
          }

          // Afficher la modale uniquement pour les images qui bénéficient de la compression
          if (filesToShowModal.length > 0) {
            pendingFiles.value = filesToShowModal;
            currentImageFile.value = filesToShowModal[0];
            displayCompressionModal.value = true;
          }
        }
      }
    };

    const handleCloseCompressionModal = () => {
      displayCompressionModal.value = false;
      currentImageFile.value = null;
      pendingFiles.value = [];
    };

    const handleImportImage = async (file: globalThis.File, isCompressed: boolean) => {
      // Retirer le fichier traité de la liste en attente
      const currentIndex = pendingFiles.value.findIndex(f => f === currentImageFile.value);
      if (currentIndex !== -1) {
        pendingFiles.value.splice(currentIndex, 1);
      }

      // Upload du fichier sélectionné (compressé ou original)
      await uploadFilesToMedia([file]);

      // S'il reste des fichiers en attente, afficher la modale pour le suivant
      if (pendingFiles.value.length > 0) {
        currentImageFile.value = pendingFiles.value[0];
      } else {
        // Tous les fichiers ont été traités, fermer la modale
        handleCloseCompressionModal();
      }
    };

    const chooseFileToUpload = () => {
      if (chooseFileBtnRef.value) {
        chooseFileBtnRef.value.$el.click();
      }
    };

    const toolbarItems: Ref<MenuItem[]> = ref([
      {
        label: t('fileManager.filesList.toolbar.newBtn.actions.newFolder'),
        icon: 'fal fa-folder',
        command: () => { displayNewFolderModal.value = true; },
      },
    ]);

    const lastFile = ref('');
    const checkNextPage = ref(true);

    const fetchData = async (path = '', nextPage = false) => {
      if (!nextPage) {
        files.value = [];
        folders.value = [];
        loading.value = true;
      } else {
        loadingNextPage.value = true;
      }

      const result = await getFilesFromPath(path, idShop, checkNextPage.value ? lastFile.value : '');
      let acceptedExtensions = props.extensions.split(',').map(ext => ext.trim());
      acceptedExtensions = acceptedExtensions.flatMap(ext => ext === 'image/*' ? extensionsList['image/*'].split(',') : ext);

      if (result.data) {
        if (result.data.files) {
          Object.entries(result.data.files)
            .forEach((row: any, name: any) => {
              if (!row[1].is_dir) {
                const index = row[0].split('.')[0];
                const url = `https://media.shopimind.io/clients/${row[1].src}`;
                if (acceptedExtensions.some((ext) => row[1].name.toString().toLowerCase().endsWith(ext.toLowerCase()))) {
                  files.value.push({
                    id: index,
                    url,
                    base_url: row[1].src,
                    type: row[1].type
                  });
                }
              } else if (row[1].is_dir && !nextPage) {
                folders.value.push({
                  id: row[1].name,
                  name: row[1].name,
                  path: row[1].path,
                });
              }
            });
        }
        lastFile.value = result.data.lastFile;
        checkNextPage.value = result.data.moreFiles;
      }
      loading.value = false;
      loadingNextPage.value = false;
    };

    // Breadcrumb
    const breadcrumbItems: Ref<MenuItem[]> = ref([]);

    const toHome = async () => {
      breadcrumbItems.value = [];
      currentPath.value = '';
      // Reinit checkNextPage and lastFile
      lastFile.value = '';
      checkNextPage.value = true;
      await fetchData();
    }

    const homeBreadcrumb: Ref<MenuItem> = ref({
      icon: 'fal fa-house',
      command: async () => {
        await toHome();
      },
    });

    const onOpenFolder = async (folder: Folder) => {
      const toFolder = async () => {
        const path = breadcrumbItems.value.map((item) => item.label).join('/');
        if (currentPath.value !== path) {
          currentPath.value = path;
          // Reinit checkNextPage and lastFile
          lastFile.value = '';
          checkNextPage.value = true;
          await fetchData(path);
        }
      }

      if (!breadcrumbItems.value.find((item) => item.key === folder.id)) {
        breadcrumbItems.value.push({
          label: folder.name,
          key: folder.id,
          command: () => {
            onOpenFolder(folder);
          }
        });
        await toFolder();
      } else {
        const targetIndex = breadcrumbItems.value.findIndex(item => item.key === folder.id);
        breadcrumbItems.value = breadcrumbItems.value.splice(0, targetIndex + 1);
        await toFolder();
      }
    };

    const folderNameValidation = ref({
      folderNameValidator: folderNameValidator(),
    });

    const createFolder = async (folderName: string, callback: Function) => {
      try {
        const newPath = currentPath.value ? `${currentPath.value}/${folderName}` : folderName;
        const { data: newData } = await createFolderInMedia(newPath, idShop);
        if (!folders.value.find((folder) => folder.name.toLowerCase().trim() === folderName.toLowerCase().trim())) {
          folders.value.push({
            id: folderName,
            name: folderName,
            path: newData.path,
          });
        }
      } catch (err) {
        showToastError(t('errorMessages.GENERIC_ERROR'));
      } finally {
        callback();
      }
    };

    const handleScroll = async () => {
      if (!loadingNextPage.value && checkNextPage.value) {
        let path = '';
        if (breadcrumbItems.value.length) {
          path = breadcrumbItems.value.map((item) => item.label).join('/');
        }
        await fetchData(path, true);
      }
    };

    const handleReturn = async () => {
      if (breadcrumbItems.value.length - 2 < 0) {
        await toHome();
      } else {
        const folderToReturnTo: Folder = {
          id: breadcrumbItems.value[breadcrumbItems.value.length - 2].key as string,
          name: breadcrumbItems.value[breadcrumbItems.value.length - 2].label as string,
          path: '',
        };
        await onOpenFolder(folderToReturnTo);
      }
    };

    onMounted(async () => {
      await fetchData();
    });

    return {
      t,
      files,
      folders,
      displaySpinner,
      chooseFileBtnRef,
      toolbarItems,
      homeBreadcrumb,
      breadcrumbItems,
      loading,
      newFolderName,
      displayNewFolderModal,
      layout,
      layoutOptions,
      loadingNextPage,
      folderNameValidation,
      displayCompressionModal,
      currentImageFile,
      detectedContext,

      handleChooseFile,
      handleUpload,
      handleDeleteItem,
      chooseFileToUpload,
      onOpenFolder,
      createFolder,
      handleScroll,
      handleReturn,
      handleMoveItem,
      handleCloseCompressionModal,
      handleImportImage,
    };
  },
});
